import React, { useState, useEffect, useRef } from 'react';
import './Support.css';
import { Link } from 'react-router-dom';
import Trial from './Trial';
import data from './Instant_Demos/supportData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHeadset, faBookOpen, faPlayCircle, faChevronDown,
  faBoxOpen, faUserPlus, faFileInvoiceDollar, faCartShopping,
  faEnvelope, faPhone, faComments, faClock,
  faGraduationCap, faLightbulb, faRocket, faShieldHalved,
  faCirclePlay, faArrowRight, faSearch, faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp, faYoutube } from '@fortawesome/free-brands-svg-icons';

// Icon map for support cards
const CARD_ICONS = {
  'Product Creation': faBoxOpen,
  'Account Creation': faUserPlus,
  'Sales Bills': faFileInvoiceDollar,
  'Purchase Bills': faCartShopping,
};

const CARD_COLORS = {
  'Product Creation': 'blue',
  'Account Creation': 'teal',
  'Sales Bills': 'purple',
  'Purchase Bills': 'amber',
};

// Learning guide videos — real Auditusierp YouTube channel videos
const LEARNING_VIDEOS = [
  {
    id: 'pos-mobile-app',
    title: 'Auditusierp POS Mobile App',
    description: 'Explore the powerful POS mobile app for billing on the go with barcode scanning and payment modes.',
    category: 'Beginner',
    color: 'blue',
    thumbnail: `https://img.youtube.com/vi/C4EYBjVqNbc/hqdefault.jpg`,
    videoUrl: 'https://www.youtube.com/embed/C4EYBjVqNbc',
  },
  {
    id: 'mobile-app-ultimate',
    title: 'Auditus-iERP Mobile App: Accounting, Inventory & Billing',
    description: 'The ultimate solution for accounting, inventory management and billing — all from your mobile device.',
    category: 'Beginner',
    color: 'teal',
    thumbnail: `https://img.youtube.com/vi/As0tAisZwHU/hqdefault.jpg`,
    videoUrl: 'https://www.youtube.com/embed/As0tAisZwHU',
  },
  {
    id: 'multicompany',
    title: 'Multicompany Working with Auditus-iERP',
    description: 'Learn how to manage multiple companies and branches seamlessly from one unified platform.',
    category: 'Advanced',
    color: 'purple',
    thumbnail: `https://img.youtube.com/vi/O1afn3a-C8A/hqdefault.jpg`,
    videoUrl: 'https://www.youtube.com/embed/O1afn3a-C8A',
  },
  {
    id: 'dashboard-analysis',
    title: 'Dashboard Sales Analysis',
    description: 'Deep dive into the sales analysis dashboard — track trends, profits, and key business metrics in real time.',
    category: 'Essentials',
    color: 'green',
    thumbnail: `https://img.youtube.com/vi/d5MC3LHy_VE/hqdefault.jpg`,
    videoUrl: 'https://www.youtube.com/embed/d5MC3LHy_VE',
  },
  {
    id: 'paint-shop-demo',
    title: 'Paint Shop Billing & Inventory Management Demo',
    description: 'See how paint shops can efficiently manage billing, inventory, and customer accounts.',
    category: 'Essentials',
    color: 'coral',
    thumbnail: `https://img.youtube.com/vi/tcxRuVGPjss/hqdefault.jpg`,
    videoUrl: 'https://www.youtube.com/embed/tcxRuVGPjss',
  },
  {
    id: 'multipayment-pos',
    title: 'POS Billing: Multipayment Mode',
    description: 'Master multi-payment billing — split payments across cash, card, UPI, and wallets seamlessly.',
    category: 'Essentials',
    color: 'amber',
    thumbnail: `https://img.youtube.com/vi/npFtyEu9Wdc/hqdefault.jpg`,
    videoUrl: 'https://www.youtube.com/embed/npFtyEu9Wdc',
  },
  {
    id: 'barcode-label',
    title: 'Barcode Label Design with Auditus-iERP',
    description: 'Design and print custom barcode labels for your products — MRP, batch, and expiry included.',
    category: 'Essentials',
    color: 'blue',
    thumbnail: `https://img.youtube.com/vi/xClW7EH2Cmk/hqdefault.jpg`,
    videoUrl: 'https://www.youtube.com/embed/xClW7EH2Cmk',
  },
  {
    id: 'supermarket-ops',
    title: 'Simplify Supermarket Operations',
    description: 'Complete walkthrough of supermarket management — POS billing, stock control, and daily reports.',
    category: 'Beginner',
    color: 'green',
    thumbnail: `https://img.youtube.com/vi/SrP7FlxPFTE/hqdefault.jpg`,
    videoUrl: 'https://www.youtube.com/embed/SrP7FlxPFTE',
  },
  {
    id: 'head-branch-office',
    title: 'Head Office & Branch Office Management',
    description: 'Set up and manage head office to branch office workflows with real-time data sync.',
    category: 'Advanced',
    color: 'purple',
    thumbnail: `https://img.youtube.com/vi/rw9Z9pMdPyc/hqdefault.jpg`,
    videoUrl: 'https://www.youtube.com/embed/rw9Z9pMdPyc',
  },
  {
    id: 'digital-card-print',
    title: 'Digital Card Printing — A, B, C, D Price Print Card',
    description: 'Effortless digital card printing with multi-tier pricing support for retail stores.',
    category: 'Advanced',
    color: 'amber',
    thumbnail: `https://img.youtube.com/vi/FvD4YZBTosU/hqdefault.jpg`,
    videoUrl: 'https://www.youtube.com/embed/FvD4YZBTosU',
  },
  {
    id: 'master-accounts',
    title: 'Multiple Master Accounts and Product Masters',
    description: 'Learn to manage multiple master accounts and product masters for organized business data.',
    category: 'Advanced',
    color: 'teal',
    thumbnail: `https://img.youtube.com/vi/-PoNBRlIPrM/hqdefault.jpg`,
    videoUrl: 'https://www.youtube.com/embed/-PoNBRlIPrM',
  },
  {
    id: 'realtime-returns',
    title: 'Real-time Returns in POS Billing',
    description: 'Handle sales returns instantly during POS billing with real-time stock and account updates.',
    category: 'Essentials',
    color: 'coral',
    thumbnail: `https://img.youtube.com/vi/d9wGwmw0mDo/hqdefault.jpg`,
    videoUrl: 'https://www.youtube.com/embed/d9wGwmw0mDo',
  },
];

// FAQ data
const FAQS = [
  {
    q: 'How do I reset my password?',
    a: 'Go to the login screen and click "Forgot Password." Enter your registered email or phone number, and a reset link will be sent to you. Follow the link to create a new password.',
  },
  {
    q: 'Can I use Auditus-iERP on multiple devices?',
    a: 'Yes! Auditus-iERP supports multi-device access. You can use the desktop software on your billing counter, the mobile app for on-the-go management, and access cloud reports from any browser.',
  },
  {
    q: 'How do I generate GST returns?',
    a: 'Navigate to Reports → GST Returns. Select the return type (GSTR-1, GSTR-3B, etc.) and the period. The system auto-populates all data from your invoices. Review and click "Generate" to create the filing-ready report.',
  },
  {
    q: 'Is my data backed up automatically?',
    a: 'Yes, Auditus-iERP performs automatic daily cloud backups on secure AWS servers. You can also trigger manual backups anytime from Settings → Backup & Restore.',
  },
  {
    q: 'How do I add a new branch to my account?',
    a: 'Go to Settings → Branch Management → Add New Branch. Fill in the branch details, assign users, and configure stock settings. The new branch will sync automatically with your central dashboard.',
  },
  {
    q: 'What payment modes are supported for billing?',
    a: 'Auditus-iERP supports Cash, Card, UPI, Bank Transfer, Credit/Debit Notes, Wallet, and Split Payment. You can also configure custom payment modes from Settings → Payment Configuration.',
  },
];

// Quick help channels
const HELP_CHANNELS = [
  { icon: faPhone, title: 'Call Us', desc: '+91 90320 32612', sub: 'Mon–Sat, 9 AM – 7 PM', color: 'blue' },
  { icon: faWhatsapp, title: 'WhatsApp', desc: 'Chat with Support', sub: 'Instant response', color: 'green' },
  { icon: faEnvelope, title: 'Email', desc: 'support@auditusierp.com', sub: 'Response within 2 hrs', color: 'purple' },
  { icon: faComments, title: 'Live Chat', desc: 'Talk to an Expert', sub: 'Available 24/7', color: 'teal' },
];

export default function Support() {
  const [showCopyPopup, setShowCopyPopup] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);


  const holdTimer = useRef(null);
  const HOLD_DURATION_MS = 5000;

  // Scroll reveal observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('sp-visible');
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.sp-reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.key === 'F9') {
        event.preventDefault();
        setShowCopyPopup(true);
      } else if (event.key === 'Escape') {
        setShowCopyPopup(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Hold-to-open for mobile
  useEffect(() => {
    const handleTouchStart = () => {
      if (window.innerWidth <= 768) {
        if (holdTimer.current) clearTimeout(holdTimer.current);
        holdTimer.current = setTimeout(() => {
          setShowCopyPopup(true);
          holdTimer.current = null;
        }, HOLD_DURATION_MS);
      }
    };
    const handleTouchEnd = () => {
      if (holdTimer.current) { clearTimeout(holdTimer.current); holdTimer.current = null; }
    };
    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);
    document.addEventListener('touchcancel', handleTouchEnd);
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchEnd);
      if (holdTimer.current) clearTimeout(holdTimer.current);
    };
  }, []);

  const handleCopyLink = (slug) => {
    const fullLink = `${window.location.origin}/creation/${slug}`;
    navigator.clipboard.writeText(fullLink)
      .then(() => { alert(`Link copied: ${fullLink}`); setShowCopyPopup(false); })
      .catch(err => { console.error('Failed to copy: ', err); alert('Failed to copy link.'); });
  };



  return (
    <div className="support-root">

      {/* ── HERO ───────────────────────────────────── */}
      <section className="sp-hero">
        <div className="sp-hero-grid" />
        <div className="sp-hero-glow sp-glow-1" />
        <div className="sp-hero-glow sp-glow-2" />
        <div className="sp-hero-inner">
          <div className="sp-hero-badge">
            <FontAwesomeIcon icon={faHeadset} />
            Support Center
          </div>
          <h1 className="sp-hero-h1">
            How Can We <span className="sp-accent">Help You</span> Today?
          </h1>
          <p className="sp-hero-sub">
            Find step-by-step guides, watch learning videos, and get instant support for Auditus-iERP.
          </p>
          <div className="sp-hero-stats">
            <div className="sp-hstat"><span className="sp-hstat-val">50+</span><span className="sp-hstat-lbl">Guides</span></div>
            <div className="sp-hstat"><span className="sp-hstat-val">24/7</span><span className="sp-hstat-lbl">Support</span></div>
            <div className="sp-hstat"><span className="sp-hstat-val">12+</span><span className="sp-hstat-lbl">Video Tutorials</span></div>
            <div className="sp-hstat"><span className="sp-hstat-val">2hr</span><span className="sp-hstat-lbl">Avg Response</span></div>
          </div>
        </div>
      </section>

      {/* ── INSTANT DEMO GUIDES ────────────────────── */}
      <section className="sp-guides-section">
        <div className="sp-section-header sp-reveal">
          <div className="sp-tag">Step-by-Step Guides</div>
          <h2 className="sp-h2">Interactive <span>Learning Demos</span></h2>
          <p className="sp-desc">Click any guide below to walk through the process step-by-step with screenshots.</p>
        </div>
        <div className="sp-guides-grid">
          {data.map((item, index) => {
            const icon = CARD_ICONS[item.title] || faBookOpen;
            const color = CARD_COLORS[item.title] || 'blue';
            return item.link ? (
              <Link to={`${item.link}/${item.slug}`} key={index} state={{ steps: item.steps }} className="sp-guide-link">
                <div className={`sp-guide-card sp-color-${color} sp-reveal`} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="sp-guide-icon-wrap">
                    <FontAwesomeIcon icon={icon} className="sp-guide-icon" />
                  </div>
                  <div className="sp-guide-content">
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                    <span className="sp-guide-steps-badge">
                      {item.steps?.length || 0} steps
                    </span>
                  </div>
                  <FontAwesomeIcon icon={faArrowRight} className="sp-guide-arrow" />
                </div>
              </Link>
            ) : (
              <div className={`sp-guide-card sp-color-${color} sp-reveal`} key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="sp-guide-icon-wrap">
                  <FontAwesomeIcon icon={icon} className="sp-guide-icon" />
                </div>
                <div className="sp-guide-content">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── LEARNING VIDEOS ────────────────────────── */}
      <section className="sp-videos-section">
        <div className="sp-section-header sp-reveal">
          <div className="sp-tag light">
            <FontAwesomeIcon icon={faYoutube} /> Video Learning Guide
          </div>
          <h2 className="sp-h2 light">Master Auditus-iERP with <span>Video Tutorials</span></h2>
          <p className="sp-desc light">Watch comprehensive tutorials to get the most out of every feature.</p>
        </div>

        <div className="sp-videos-grid">
          {LEARNING_VIDEOS.map((video, idx) => (
            <div
              key={video.id}
              className={`sp-video-card sp-vcolor-${video.color} sp-reveal`}
              style={{ animationDelay: `${idx * 0.08}s` }}
            >
              <div className="sp-video-iframe-wrap">
                <iframe
                  src={video.videoUrl}
                  title={video.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                />
              </div>
              <div className="sp-video-info">
                <h3>{video.title}</h3>
                <p>{video.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FAQ ────────────────────────────────────── */}
      <section className="sp-faq-section">
        <div className="sp-section-header sp-reveal">
          <div className="sp-tag"><FontAwesomeIcon icon={faQuestionCircle} /> FAQ</div>
          <h2 className="sp-h2">Frequently Asked <span>Questions</span></h2>
          <p className="sp-desc">Quick answers to the most common queries about Auditus-iERP.</p>
        </div>
        <div className="sp-faq-list">
          {FAQS.map((faq, i) => (
            <div
              key={i}
              className={`sp-faq-item ${openFaq === i ? 'open' : ''}`}
            >
              <button className="sp-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                <span>{faq.q}</span>
                <FontAwesomeIcon icon={faChevronDown} className="sp-faq-chevron" />
              </button>
              <div className="sp-faq-a">
                <p>{faq.a}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── QUICK HELP ─────────────────────────────── */}
      <section className="sp-help-section">
        <div className="sp-section-header sp-reveal">
          <div className="sp-tag light"><FontAwesomeIcon icon={faClock} /> Get Help</div>
          <h2 className="sp-h2 light">Need <span>Immediate Help?</span></h2>
          <p className="sp-desc light">Reach our support team through any of these channels.</p>
        </div>
        <div className="sp-help-grid">
          {HELP_CHANNELS.map((ch, i) => (
            <div key={i} className={`sp-help-card sp-hcolor-${ch.color} sp-reveal`} style={{ animationDelay: `${i * 0.1}s` }}>
              <div className="sp-help-icon-wrap">
                <FontAwesomeIcon icon={ch.icon} />
              </div>
              <h3>{ch.title}</h3>
              <p className="sp-help-val">{ch.desc}</p>
              <p className="sp-help-sub">{ch.sub}</p>
            </div>
          ))}
        </div>
      </section>

      <Trial />



      {/* Copy Link Popup */}
      {showCopyPopup && (
        <div className="copy-link-popup-overlay" onClick={() => setShowCopyPopup(false)}>
          <div className="copy-link-popup-content" onClick={(e) => e.stopPropagation()}>
            <h2>Copy Demo Link</h2>
            <p>Click a button below to copy the link for the specific demo.</p>
            <div className="link-buttons-container">
              {data.map((item, index) => (
                <button key={index} className="copy-link-button" onClick={() => handleCopyLink(item.slug)}>
                  {item.title} ({item.slug})
                </button>
              ))}
            </div>
            <button className="close-popup-button" onClick={() => setShowCopyPopup(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}